import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Sparkles,
  Plus,
  Compass,
  Settings as SettingsIcon,
  ListChecks,
  GitMerge,
  TrendingUp,
  Megaphone,
  RefreshCw,
  X as XIcon,
  type LucideIcon,
} from "lucide-react";
import { api, type SearchHit } from "../lib/api";

interface Command {
  id: string;
  label: string;
  hint: string;
  Icon: LucideIcon;
  run: () => void | Promise<void>;
}

/** Cmd-K command palette. Two modes:
 *    - command mode (no leading prefix): jump to a tab / mode + run
 *      a quick action (run brief, run monitors, etc.)
 *    - search mode (anything else): live FTS5 search across captures
 *      + entities; pick a hit to navigate. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [suggests, setSuggests] = useState<
    Array<{ id: string; name: string; kind: string; sub: string; route: string }>
  >([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Keyboard shortcut: Cmd+K (Mac) / Ctrl+K (Windows / Linux). Stops
  // when an editable element has focus so we don't hijack the user's
  // typing in textareas.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        const target = e.target as HTMLElement | null;
        const inEditable = target && (
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable
        );
        if (inEditable && !open) return;
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    if (!open) {
      setQuery("");
      setHits([]);
      setSuggests([]);
    }
  }, [open]);

  // Entity jump suggestions (Wave 4 item 6). Fast heuristic
  // server-side prefix match across people / orgs / places /
  // projects / topics / things / dated events, returned with a
  // precise deep-link route so the palette navigates straight to
  // the entity instead of a generic /explore.
  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setSuggests([]);
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        const r = await fetch(
          `/api/search/suggest?q=${encodeURIComponent(query.trim())}`,
          { credentials: "same-origin" },
        );
        const j = await r.json();
        setSuggests(Array.isArray(j.results) ? j.results : []);
      } catch {
        setSuggests([]);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [query, open]);

  // Debounced FTS5 search. Skip when the query looks like a command
  // (short + matches one of the static commands).
  useEffect(() => {
    if (!open) return;
    if (query.length < 2) {
      setHits([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.search(query, 8);
        setHits(r.hits);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [query, open]);

  const commands = useMemo<Command[]>(() => [
    { id: "today", label: "Today", hint: "Cockpit", Icon: Sparkles, run: () => navigate("/") },
    { id: "capture", label: "Capture", hint: "Drop a thought", Icon: Plus, run: () => navigate("/capture") },
    { id: "explore", label: "Explore", hint: "All lenses", Icon: Compass, run: () => navigate("/explore") },
    { id: "settings", label: "Settings", hint: "Account, agents, connectors", Icon: SettingsIcon, run: () => navigate("/settings") },
    { id: "backlog", label: "Backlog", hint: "Batch jobs", Icon: ListChecks, run: () => navigate("/backlog") },
    { id: "reconcile", label: "Reconciliation", hint: "Diff-review queue", Icon: GitMerge, run: () => navigate("/reconcile") },
    { id: "evolution", label: "Evolution", hint: "Self-improvement queue", Icon: TrendingUp, run: () => navigate("/evolution") },
    { id: "whatsnew", label: "What's new", hint: "Changelog feed", Icon: Megaphone, run: () => navigate("/settings?section=whatsnew") },
    {
      id: "brief",
      label: "Run daily brief now",
      hint: "Triggers the brief agent",
      Icon: RefreshCw,
      run: () => api.briefRunNow().then(() => undefined).catch(() => undefined),
    },
    {
      id: "coach",
      label: "Run capture coach",
      hint: "Generate suggestions",
      Icon: Sparkles,
      run: () => fetch("/api/coach/run-now", { method: "POST", credentials: "same-origin" }).then(() => undefined).catch(() => undefined),
    },
  ], [navigate]);

  const matchedCommands = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      c.label.toLowerCase().includes(q) || c.hint.toLowerCase().includes(q),
    );
  }, [commands, query]);

  if (!open) return null;

  const close = () => setOpen(false);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-12"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type to search captures + entities, or run a command…"
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="w-7 h-7 rounded-full inline-flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {matchedCommands.length > 0 && (
            <section className="px-2 py-2">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                Commands
              </div>
              <ul>
                {matchedCommands.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={async () => {
                        await c.run();
                        close();
                      }}
                      className="w-full text-left flex items-center gap-2.5 px-2 py-2 rounded-md text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <c.Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      <span className="flex-1 truncate">{c.label}</span>
                      <span className="text-[11px] text-slate-400 truncate">{c.hint}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {suggests.length > 0 && (
            <section className="px-2 py-2 border-t border-slate-200 dark:border-slate-700">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                Jump to
              </div>
              <ul>
                {suggests.map((s) => (
                  <li key={`${s.kind}-${s.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(s.route);
                        close();
                      }}
                      className="w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex-shrink-0">
                          {s.kind}
                        </span>
                        <span className="text-slate-900 dark:text-slate-100 font-medium truncate">
                          {s.name}
                        </span>
                        {s.sub && (
                          <span className="text-[11px] text-slate-400 truncate">
                            {s.sub}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {hits.length > 0 && (
            <section className="px-2 py-2 border-t border-slate-200 dark:border-slate-700">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                Search hits {searching ? "(loading…)" : ""}
              </div>
              <ul>
                {hits.map((h) => (
                  <li key={`${h.kind}-${h.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        if (h.kind === "entity") {
                          navigate(`/explore`);
                        } else {
                          navigate(`/`);
                        }
                        close();
                      }}
                      className="w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 flex-shrink-0">
                          {h.kind === "entity" ? h.entityKind : "capture"}
                        </span>
                        <span className="text-slate-900 dark:text-slate-100 font-medium truncate">
                          {h.kind === "entity" ? h.name : (h.snippet || h.text)}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {query.length >= 2 && hits.length === 0 && suggests.length === 0 && !searching && matchedCommands.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400 italic text-center">
              No matches.
            </div>
          )}
        </div>
        <footer className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-400 flex items-center justify-between">
          <span>Press <kbd className="px-1 rounded bg-slate-100 dark:bg-slate-700">Esc</kbd> to close</span>
          <span><kbd className="px-1 rounded bg-slate-100 dark:bg-slate-700">Cmd</kbd> + <kbd className="px-1 rounded bg-slate-100 dark:bg-slate-700">K</kbd> to toggle</span>
        </footer>
      </div>
    </div>
  );
}
